import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  LuCode, LuSmartphone, LuBrain, LuRefreshCw, LuServer, 
  LuTestTube, LuCloud, LuChartBar, LuArrowRight, LuMonitor,
  LuMegaphone, LuPalette, LuSearch, LuMail, LuUsers, LuTarget
} from 'react-icons/lu';

const services = [
  {
    icon: LuCode,
    title: 'Custom Software Development',
    description: 'We are a reputed software development company that provides creative solutions tailored to your unique business needs.',
    color: 'blue'
  },
  {
    icon: LuSmartphone,
    title: 'Application Development',
    description: 'Powered by clients unique needs, we customize and deliver unique mobile and web application solutions.',
    color: 'orange'
  },
  {
    icon: LuBrain,
    title: 'AI as a Service',
    description: 'Unlock doors to success with AI. We provide cutting-edge AI solutions that transform businesses.',
    color: 'blue'
  },
  {
    icon: LuRefreshCw,
    title: 'Application Modernization',
    description: 'Modernize apps built for any platform with dedicated legacy system modernisation services.',
    color: 'orange'
  },
  {
    icon: LuServer,
    title: 'ServiceNow Implementation',
    description: 'As a recognized software development company, we help firms utilize the full power of the ServiceNow platform.',
    color: 'blue'
  },
  {
    icon: LuTestTube,
    title: 'Software Testing Services',
    description: 'Having access to the latest software testing tools, we help clients debug and secure apps.',
    color: 'orange'
  },
  {
    icon: LuCloud,
    title: 'DevOps Implementation',
    description: 'We offer a holistic approach to DevSecOps that integrates security seamlessly into development.',
    color: 'blue'
  },
  {
    icon: LuChartBar,
    title: 'Data Science',
    description: 'Accelerate generation and adoption of actionable insights through mature data science practices.',
    color: 'orange'
  },
  {
    icon: LuMonitor,
    title: 'Cloud Deployment',
    description: 'We offer scalable, secure, and reliable solutions for data storage, backup, and cloud infrastructure.',
    color: 'blue'
  },
  {
    icon: LuMegaphone,
    title: 'Digital Marketing',
    description: 'Drive growth with data-driven digital marketing strategies including SEO, PPC, and social media marketing.',
    color: 'orange'
  },
  {
    icon: LuPalette,
    title: 'Brand Building',
    description: 'Create a powerful brand identity with our comprehensive branding services from logo design to brand strategy.',
    color: 'blue'
  },
  {
    icon: LuSearch,
    title: 'SEO & Content Marketing',
    description: 'Boost your online visibility with our SEO expertise and engaging content marketing strategies.',
    color: 'orange'
  }
];

const serviceAreas = [
  {
    title: 'Product Engineering',
    items: ['Software Engineering', 'Mobile Engineering', 'Product Development']
  },
  {
    title: 'Artificial Intelligence',
    items: ['AI Development', 'AI As A Service', 'ML Development']
  },
  {
    title: 'Data Engineering',
    items: ['Data Science Engineering', 'Data Analytics', 'Data Governance']
  },
  {
    title: 'Cloud & DevOps',
    items: ['Cloud Consulting', 'DevOps Consulting', 'Infrastructure Management']
  },
  {
    title: 'Strategy & Design',
    items: ['Product Strategy Consulting', 'Experience Design Consulting', 'UX Research']
  },
  {
    title: 'Application Modernization',
    items: ['Legacy App Modernization', 'Software Maintenance & Support', 'Migration Services']
  },
  {
    title: 'Systems Implementation',
    items: ['ServiceNow', 'Microsoft Enterprise Development', 'Salesforce']
  },
  {
    title: 'Quality Engineering',
    items: ['Software Testing', 'Test Automation', 'Performance Testing']
  },
  {
    title: 'Digital Marketing',
    items: ['SEO Optimization', 'Social Media Marketing', 'PPC Advertising']
  },
  {
    title: 'Brand Building',
    items: ['Brand Strategy', 'Visual Identity', 'Content Creation']
  }
];

const marketingServices = [
  {
    icon: LuMegaphone,
    title: 'Social Media Marketing',
    description: 'Engage your audience across all major social platforms with strategic content and community management.'
  },
  {
    icon: LuSearch,
    title: 'Search Engine Optimization',
    description: 'Improve your search rankings with technical SEO, content optimization, and link building strategies.'
  },
  {
    icon: LuTarget,
    title: 'Pay-Per-Click Advertising',
    description: 'Maximize ROI with targeted Google Ads, Facebook Ads, and programmatic advertising campaigns.'
  },
  {
    icon: LuMail,
    title: 'Email Marketing',
    description: 'Nurture leads and drive conversions with personalized email campaigns and marketing automation.'
  },
  {
    icon: LuUsers,
    title: 'Influencer Marketing',
    description: 'Leverage influencer partnerships to expand your reach and build authentic brand connections.'
  },
  {
    icon: LuPalette,
    title: 'Content Marketing',
    description: 'Create compelling content that educates, engages, and converts your target audience.'
  }
];

export default function Services() {
  return (
    <div className="bg-slate-950" data-testid="services-page">
      <section className="pt-32 pb-16 md:pb-24 relative overflow-hidden" data-testid="services-hero">
        <div className="absolute inset-0">
          <motion.div
            animate={{ scale: [1, 1.2, 1], x: [0, 30, 0] }}
            transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-20 left-10 w-[400px] h-[400px] bg-blue-600/10 rounded-full blur-3xl"
          />
          <motion.div
            animate={{ scale: [1, 1.3, 1], x: [0, -30, 0] }}
            transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
            className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-orange-600/10 rounded-full blur-3xl"
          />
        </div>
        
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center space-y-4"
          >
            <span className="text-sm font-medium text-blue-400 uppercase tracking-widest">Our Services</span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-tight">
              Accelerating Innovation With{' '}
              <span className="text-gradient-orange">Solutions Built for Speed</span>
            </h1>
            <p className="text-base md:text-lg text-slate-300 leading-relaxed max-w-3xl mx-auto">
              We deliver strategic value for businesses worldwide. From custom software development to digital marketing - we help transform visionary ideas into reality.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24" data-testid="services-grid">
        <div className="container mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Our <span className="text-gradient-blue">Core Services</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Comprehensive technology and digital solutions tailored to your business needs
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              const isOrange = service.color === 'orange';
              return (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05, duration: 0.5 }}
                  className={`bg-slate-900/50 rounded-2xl p-6 border transition-all group ${
                    isOrange 
                      ? 'border-orange-500/20 hover:border-orange-500/50' 
                      : 'border-blue-500/20 hover:border-blue-500/50'
                  }`}
                  data-testid={`service-card-${index}`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                    isOrange ? 'bg-orange-500/10 border border-orange-500/30' : 'bg-blue-500/10 border border-blue-500/30'
                  }`}>
                    <Icon size={24} className={isOrange ? 'text-orange-400' : 'text-blue-400'} />
                  </div>
                  
                  <h3 className={`font-heading text-lg font-semibold text-white mb-2 transition-colors ${
                    isOrange ? 'group-hover:text-orange-400' : 'group-hover:text-blue-400'
                  }`}>
                    {service.title}
                  </h3>
                  
                  <p className="text-slate-400 text-sm leading-relaxed">
                    {service.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-slate-900/30 border-y border-slate-800" data-testid="digital-marketing-section">
        <div className="container mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="text-sm font-medium text-orange-400 uppercase tracking-widest">Digital Growth</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mt-2 mb-4">
              Digital Marketing & <span className="text-gradient-orange">Brand Building</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Accelerate your growth with our comprehensive digital marketing solutions
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {marketingServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  className="bg-slate-800/30 rounded-2xl p-6 border border-slate-700 hover:border-orange-500/50 transition-all group"
                  data-testid={`marketing-card-${index}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500/20 to-orange-600/10 border border-orange-500/30 flex items-center justify-center mb-4">
                    <Icon size={24} className="text-orange-400" />
                  </div>
                  
                  <h3 className="font-heading text-lg font-semibold text-white mb-2 group-hover:text-orange-400 transition-colors">
                    {service.title}
                  </h3>
                  
                  <p className="text-slate-400 text-sm leading-relaxed">
                    {service.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24" data-testid="service-areas">
        <div className="container mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Our <span className="text-gradient-blue">Service Areas</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Comprehensive solutions across multiple domains
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6 md:gap-8">
            {serviceAreas.map((area, index) => (
              <motion.div
                key={area.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05, duration: 0.5 }}
                className="group"
                data-testid={`service-area-${index}`}
              >
                <h3 className="text-orange-400 font-heading text-sm md:text-base font-bold mb-3 group-hover:text-orange-300 transition-colors">
                  {area.title}
                </h3>
                <ul className="space-y-1.5">
                  {area.items.map((item, i) => (
                    <li key={i} className="text-slate-400 text-xs md:text-sm hover:text-white transition-colors cursor-pointer">
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-gradient-to-b from-slate-900/30 via-orange-950/10 to-slate-900/30" data-testid="services-cta">
        <div className="container mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center space-y-6"
          >
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white">
              Ready to Start Your Project?
            </h2>
            <p className="text-base md:text-lg text-slate-300">
              Let's discuss how we can help bring your vision to life
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-500 hover:to-orange-600 text-white px-10 py-4 rounded-full text-lg font-semibold transition-all shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 group"
              data-testid="services-cta-button"
            >
              <span>Get Started</span>
              <LuArrowRight className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
